import Controller, { inject } from '@ember/controller';
import { action } from '@ember/object';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import { cached } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { ActivityLogEvent } from 'webapp/enums/activity-log';

import type DesignerController from 'webapp/controllers/designer';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type BoardState from 'webapp/models-custom/board-state';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';
import type BoardRecord from 'webapp/models/board';

export default class DesignerBoardController extends Controller {
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('billing') declare billingService: Service.Billing;

	@inject('designer') declare designerController: DesignerController;

	get boardState(): BoardState {
		return this.designerController.boardState!;
	}

	get loop(): BoardLoop | null {
		return this.designerController.loop;
	}

	exitBoardBcosDeleted(): void {
		this.navigationService.transitionToRoute('boards');
		this.infolayerService.error('This dashboard has been deleted.');
	}

	_board: Board | null = null;

	@cached
	get board(): Board | null {
		const record = this.model as BoardRecord;
		if (record?.isDeleted) {
			once(this, this.exitBoardBcosDeleted);
			return null;
		} else {
			let board: Board;
			if (record?.id) {
				board = this.mmBoardsService.boardModels.find((item) => item.id === record.id)!;
			} else {
				board = this.mmBoardsService.createModelFromRecord(record); // in case it's new without id yet
			}

			if (board !== this._board) {
				// eslint-disable-next-line ember/no-side-effects
				this._board = board;
				next(() => {
					this.designerController.updateBoard(board);
					if (this._board?.id && !this._board.isTemplate) {
						this.mmActivityLogsService.createAndSaveActivityLogItem(
							ActivityLogEvent.BOARD_VIEW,
							this._board.id,
						);
					}
				});
			}
		}
		return this._board;
	}

	get showLoopBox(): boolean {
		return this.billingService.boardLoopsEnabled && (!!this.loop || !this.board?.isTemplate);
	}

	@action
	saveBoard(): void {
		this.designerController.saveBoard();
	}

	@action
	deleteBoard(): void {
		this.designerController.deleteBoard();
	}

	@action
	copyBoard(): void {
		this.designerController.copyBoard();
	}

	@action
	datawallGridChange(datawallGrid: DatawallGrid | null): void {
		this.designerController.activeDatawallGrid = datawallGrid;
	}

	@action
	openLoop(loop: BoardLoop): void {
		this.designerController.openLoop(loop);
	}

	@action
	openBoard(board: Board): void {
		if (board.canCurrentUserEdit) {
			this.designerController.openBoard(board);
		} else {
			this.infolayerService.error('You are not allowed to customize this dashboard.');
		}
	}

	@action
	updateLoopFromBoard(): void {
		this.designerController.updateLoopFromBoard(this.board);
	}
}
